import { useAppSelector } from "core/store/hooks";
import { FileDocument } from "models/files";
import { invitationInvitationSelector } from "pages/invitation/_redux/selectors";
import React, { useEffect } from "react";
import { Form } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import styled from "styled-components";
import BftButton from "../button";
import WebcamCapture from "../webcam-capture";
import "./style.scss";

const getColor = (props: any) => {
	if (props.isDragAccept) {
		return "#00e676";
	}
	if (props.isDragReject) {
		return "#ff1744";
	}
	if (props.isFocused) {
		return "#2196f3";
	}
	return "#bdb7c5";
};

const Container = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: baseline;
	padding: 20px;
	border-width: 2px;
	border-radius: 10px;
	border-color: ${(props: any) => getColor(props)};
	border-style: dashed;
	background-color: #f3f6f6;
	color: #bdbdbd;
	outline: none;
	max-width: 100%;
	max-height: 700px;
	transition: border 0.24s ease-in-out;
`;

const Button = styled.button`
	border-radius: 15px;
	padding-left: 30px;
	padding-right: 30px;
	padding-top: 10px;
	padding-bottom: 10px;
`;

const maxSize = 10 * 1024 * 1024;

interface Props {
	label: string;
	idTipoDocumento?: string;
	file?: FileDocument;
	isInvalid?: boolean | undefined;
	idSoggetto?: string;
	trascinaDocumento?: string;
	onChange?: (file: FileDocument | null) => void;
}

const formatiAccettati = {
	"application/pdf": [".pdf"],
	"image/jpeg": [".jpg", ".jpeg"],
	"image/png": [".png"],
};

const BftDropzone: React.FC<Props> = (props) => {
	const {
		label,
		idTipoDocumento,
		isInvalid,
		file: _file,
		idSoggetto,
		onChange,
		trascinaDocumento,
	} = props;

	const [files, setFiles] = React.useState<FileDocument[]>([]);

	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

	const invitation = useAppSelector(invitationInvitationSelector);

	const checkDisabled = (files: any) => {
		const isDisabled = files && files.length > 0;
		return isDisabled;
	};

	const handleCancel = () => {
		setFiles([]);
		setErrorMessage(null);
		onChange?.(null);
	};

	const handleCaptureFromWebcam = (image: File) => {
		if (!idTipoDocumento) throw "idTipoDocumento not found";

		Object.assign(image, {
			idTipoDocumento: idTipoDocumento,
			idTipoAllegato: image.type,
			fileimage: URL.createObjectURL(image),
			datetime: new Date(image.lastModified).toLocaleString("en-IN"),
			idSoggetto: idSoggetto,
		});

		setFiles([image as FileDocument]);
	};

	const {
		getRootProps,
		getInputProps,
		open,
		acceptedFiles,
		isFocused,
		isDragAccept,
		isDragReject,
	} = useDropzone({
		accept: {
			"application/pdf": [".pdf"],
			"image/jpeg": [".jpg", ".jpeg"],
			"image/png": [".png"],
		},
		maxFiles: 1,
		multiple: false,
		noClick: true,
		noKeyboard: true,
		onDrop: (acceptedFiles, fileRejections, event) => {
			if (event && checkDisabled(files)) {
				event.preventDefault();
				event.stopPropagation();
				return;
			}

			setErrorMessage(null);

			if (checkDisabled(files)) {
				return;
			}

			if (event && checkDisabled(files)) {
				event.preventDefault();
				event.stopPropagation();
				return;
			}

			if (acceptedFiles.length > 0) {
				const file = acceptedFiles[0];
				const isAcceptedFormat = Object.values(formatiAccettati)
					.flat()
					.some((ext) => file.name.toLowerCase().endsWith(ext));

				if (!isAcceptedFormat) {
					setErrorMessage("FORMATO NON SUPPORTATO");
					return;
				}

				if (file.size > maxSize) {
					setErrorMessage("Il file è troppo grande. Il limite è di 10 MB.");
					return;
				}

				const reader = new FileReader();

				reader.onloadend = () => {
					setFiles([
						Object.assign(file, {
							idTipoDocumento: idTipoDocumento,
							idTipoAllegato: file.type,
							fileimage: reader.result,
							datetime: new Date(file.lastModified).toLocaleString("en-IN"),
							idSoggetto: idSoggetto,
						}) as FileDocument,
					]);
				};

				if (acceptedFiles.length > 0) {
					reader.readAsDataURL(file);
				}
			}
		},

		onDragOver: (event) => {
			event.preventDefault();
			event.stopPropagation();
		},
		onDragLeave: (event) => {
			event.preventDefault();
			event.stopPropagation();
		},
		preventDropOnDocument: !checkDisabled(files),
		noDragEventsBubbling: checkDisabled(files),
		noDrag: checkDisabled(files),
	});

	useEffect(() => {
		if (_file) files?.push(_file);
	}, [_file]);

	useEffect(() => {
		if (files) onChange?.(files[0]);
	}, [files]);

	return (
		<Form.Group className="container bft-dropzone">
			{label && (
				<Form.Label>
					<h6 className="my-3">{label}</h6>
				</Form.Label>
			)}
			<Container
				{...getRootProps({
					isFocused,
					isDragAccept,
					isDragReject,
					acceptedFiles,
					className: "dropzone disabled",
					label,
				})}
				className={isInvalid ? "invalid" : ""}
			>
				<input {...getInputProps()} />
				{files.length == 0 && (
					<>
						<p>Trascina {trascinaDocumento} qui per allegarlo</p>
						<div className="d-flex flex-column">
							{invitation?.idToken && (
								<div className="mb-2">
									<WebcamCapture onCapturePhoto={handleCaptureFromWebcam} />
								</div>
							)}
							<BftButton className="btn-buffetti  " type="button" onClick={open}>
								Sfoglia
							</BftButton>
						</div>
						<span className="text-danger mt-3">{errorMessage && errorMessage}</span>
					</>
				)}

				{files.length > 0 && (
					<>
						<p className="ellipsis-20">{files?.[0]?.name}</p>

						<BftButton
							className="btn-buffetti-secondary"
							type="button"
							onClick={() => handleCancel()}
						>
							Annulla
						</BftButton>
					</>
				)}
			</Container>
			{acceptedFiles.length > 0 && acceptedFiles[0].size > maxSize && (
				<p className="alert alert-danger mt-2">Il file è troppo grande. Il limite è di 10 MB.</p>
			)}
		</Form.Group>
	);
};

export default BftDropzone;
